import { getOrdinalNum } from "./utils/get-orginal-num.js";
import { parseRRule } from "./utils/rrule-codec.js";
import { formatClockHours, formatRRuleWallClockRange } from "./utils/wall-clock-time.js";
import { RRULE_WEEKDAYS } from "./utils/schedule.js";
import { formatLength } from "./utils/format-length.js";
import { useMemo } from "react";
import { Tag, WeekDays } from "@therms/atalaya";
import { jsx, jsxs } from "react/jsx-runtime";
import classnames from "classnames";
//#region src/components/RRule/RRuleTag.tsx
var EMPTY_WEEK = {
	friday: false,
	monday: false,
	saturday: false,
	sunday: false,
	thursday: false,
	tuesday: false,
	wednesday: false
};
var getWeekFromParts = (parsed) => {
	if (!parsed) return EMPTY_WEEK;
	if (parsed.freq === "DAILY") return {
		friday: true,
		monday: true,
		saturday: true,
		sunday: true,
		thursday: true,
		tuesday: true,
		wednesday: true
	};
	const week = { ...EMPTY_WEEK };
	RRULE_WEEKDAYS.forEach((day) => {
		week[day.key] = parsed.byDay.includes(day.rrule);
	});
	return week;
};
var RRuleTag = ({ className, durationMin, rruleString, timezoneLabel }) => {
	const parsed = useMemo(() => parseRRule(rruleString), [rruleString]);
	const monthDays = useMemo(() => {
		if (parsed?.byMonthDay?.length) return parsed.byMonthDay.slice().sort((a, b) => a - b).map((md) => getOrdinalNum(md));
		return [];
	}, [parsed?.byMonthDay]);
	const weekDays = useMemo(() => getWeekFromParts(parsed), [parsed]);
	const isMonthly = parsed?.freq === "MONTHLY";
	const hour = parsed ? formatClockHours([parsed.byHour], parsed.byMinute) : "";
	const label = timezoneLabel;
	const range = useMemo(() => {
		if (!durationMin || !parsed) return null;
		const formatted = formatRRuleWallClockRange([parsed.byHour], parsed.byMinute, durationMin);
		if (!formatted) return null;
		const allDay = durationMin >= 1440;
		return {
			...formatted,
			lengthLabel: allDay ? "All day" : formatLength(durationMin),
			lengthTooltip: `This window runs for ${formatLength(durationMin)}`,
			wraps: formatted.wraps && !allDay
		};
	}, [durationMin, parsed]);
	if (!parsed) return null;
	return /* @__PURE__ */ jsxs("div", {
		className: classnames(className, "flex flex-wrap items-baseline gap-px"),
		children: [
			isMonthly ? /* @__PURE__ */ jsx("span", {
				className: "contents",
				children: monthDays.map((monthDay) => /* @__PURE__ */ jsx("span", {
					className: "bg-neutral-faded p-xxs w-[2.7rem] text-center font-bold first:rounded-l last:rounded-r",
					children: monthDay
				}, monthDay))
			}) : /* @__PURE__ */ jsx(WeekDays, { value: weekDays }),
			range ? /* @__PURE__ */ jsxs("span", {
				className: "contents",
				children: [
					/* @__PURE__ */ jsx("span", {
						className: "text-color-subtle mx-xxs font-medium",
						children: "from"
					}),
					/* @__PURE__ */ jsx("span", {
						className: "text-color-strong font-medium",
						children: range.start
					}),
					/* @__PURE__ */ jsx("span", {
						className: "text-color-subtle mx-xxs font-medium",
						children: "to"
					}),
					/* @__PURE__ */ jsx("span", {
						className: "text-color-strong font-medium",
						children: range.end
					}),
					/* @__PURE__ */ jsx("span", {
						className: "ml-xs",
						children: /* @__PURE__ */ jsx(Tag, {
							tooltip: range.lengthTooltip,
							value: range.lengthLabel,
							variant: "info"
						})
					}),
					range.wraps && /* @__PURE__ */ jsx("span", {
						className: "ml-xxs",
						children: /* @__PURE__ */ jsx(Tag, {
							tooltip: "This window ends the next day",
							value: "Overnight",
							variant: "caution"
						})
					})
				]
			}) : /* @__PURE__ */ jsxs("span", {
				className: "contents",
				children: [/* @__PURE__ */ jsx("span", {
					className: "text-color-subtle mx-xxs font-medium",
					children: " at "
				}), /* @__PURE__ */ jsx("span", {
					className: "text-color-strong font-medium",
					children: hour
				})]
			}),
			hour && label && /* @__PURE__ */ jsx("span", {
				className: "text-color-subtle ml-xs text-xs",
				children: label
			})
		]
	});
};
//#endregion
export { RRuleTag };

//# sourceMappingURL=RRuleTag.js.map